import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';
import { api } from '@shared/api';
import { helpdeskTaskSchema } from './commands';
import { helpdeskStatuses } from '../model/search';
import { isSupportedUtcInstant } from '../model/date-time';

export const taskPatchSchema = z
  .object({
    status: z.enum(helpdeskStatuses).optional(),
    feedbackCategoryId: z.uuid().nullable().optional(),
    dueAt: z.iso
      .datetime({ offset: true })
      .regex(/T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/u)
      .refine((value) => isSupportedUtcInstant(Date.parse(value)))
      .nullable()
      .optional(),
    assigneeIds: z
      .array(z.uuid())
      .refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length)
      .transform((ids) => ids.map((id) => id.toLowerCase()).sort())
      .optional(),
  })
  .strict()
  .refine(
    (body) =>
      body.status !== undefined ||
      body.feedbackCategoryId !== undefined ||
      body.dueAt !== undefined ||
      body.assigneeIds !== undefined
  );
const receiptSchema = z.object({ data: z.object({ id: z.uuid() }) });
const detailSchema = z.object({ data: helpdeskTaskSchema });
const strongEtagSchema = z.string().regex(/^"[\x21\x23-\x7e\x80-\xff]*"$/u);
export type TaskPatch = NonNullable<
  operations['patchApiV1TasksByTaskUuid']['requestBody']
>['content']['application/merge-patch+json'];

/** Send one validated command with the caller-owned retry identity. */
export function patchHelpdeskTask(
  taskUuid: string,
  input: { body: TaskPatch; etag: string; idempotencyKey: string }
) {
  return api.patch(
    `/api/v1/tasks/${encodeURIComponent(z.uuid().parse(taskUuid))}`,
    taskPatchSchema.parse(input.body),
    {
      schema: receiptSchema,
      headers: {
        'Content-Type': 'application/merge-patch+json',
        'If-Match': strongEtagSchema.parse(input.etag),
        'Idempotency-Key': input.idempotencyKey,
      },
    }
  );
}

/** Load a fresh edit snapshot; list and thread data do not carry an edit validator. */
export function helpdeskEditQuery(viewerAccessScope: string, taskUuid: string) {
  return {
    queryKey: ['helpdesk', viewerAccessScope, 'edit', taskUuid] as const,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const response = await api.getWithMetadata(`/api/v1/tasks/${encodeURIComponent(taskUuid)}`, {
        schema: detailSchema,
        signal,
      });
      return { ...response.data, etag: strongEtagSchema.parse(response.headers.get('ETag')) };
    },
    retry: false as const,
    staleTime: 0,
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  };
}
